import { MatchStatus, SPORT } from "service";

export type FinishedMatchMeta = {
  sport: SPORT;
  expiresAt: number;
};

export type MinuteDisplay = {
  topText: string;
  bottomText: string | null;
  responsiveTopText?: {
    mobile: string;
    desktop: string;
  };
};

const DEFAULT_MINUTE_DISPLAY: MinuteDisplay = {
  topText: "--",
  bottomText: null,
};

export const FINISHED_MATCH_TTL_MS = 10 * 60_000;

const FINISHED_STATUS_CODES: Partial<Record<SPORT, string[]>> = {
  [SPORT.FOOTBALL]: ["1017", "1021"],
  [SPORT.BASKETBALL]: ["1084"],
  [SPORT.TENNIS]: ["1116", "1117"],
  [SPORT.VOLLEYBALL]: ["1261"],
  [SPORT.BASEBALL]: ["1244"],
  [SPORT.ICE_HOCKEY]: ["1282"],
};

const ALL_FINISHED_CODES = Object.values(FINISHED_STATUS_CODES).reduce<
  string[]
>((acc, codes) => {
  if (Array.isArray(codes)) acc.push(...codes);
  return acc;
}, []);

const GENERIC_FINISHED_CODES = new Set(ALL_FINISHED_CODES);

const FINISHED_STATUS_KEYWORDS = [
  "full time",
  "match completed",
  "match finished",
  "end of match",
  "final whistle",
];

const HALF_TIME_STATUS_CODES: Partial<Record<SPORT, string[]>> = {
  [SPORT.FOOTBALL]: ["1015", "1019"],
  [SPORT.BASKETBALL]: ["1083"],
};

const HALF_TIME_KEYWORDS = ["half time", "half-time"];

export const isMatchFinished = (
  status: MatchStatus | undefined,
  fallbackSport?: SPORT
) => {
  if (!status) return false;
  const sport = status.s ?? fallbackSport;
  const normalizedCode =
    typeof status?.nsc === "string" ? status.nsc.trim() : undefined;

  if (sport && normalizedCode) {
    const sportCodes = FINISHED_STATUS_CODES[sport];
    if (sportCodes?.includes(normalizedCode)) return true;
  } else if (normalizedCode && GENERIC_FINISHED_CODES.has(normalizedCode)) {
    return true;
  }

  const statusName = typeof status?.stn === "string" ? status.stn : status.sts;
  if (typeof statusName === "string" && statusName.trim().length > 0) {
    const lowerName = statusName.toLowerCase();
    if (
      FINISHED_STATUS_KEYWORDS.some((keyword) => lowerName.includes(keyword))
    ) {
      return true;
    }
  }

  return false;
};

const splitSetScoreLabel = (label: string): MinuteDisplay | null => {
  const match = label.match(/^(Set\s+\d+)\s+(\d+-\d+)$/i);
  if (!match) return null;
  return { topText: match[1], bottomText: match[2] };
};

const splitBasketballLabel = (label: string): MinuteDisplay | null => {
  const tokens = label.split(/\s+/);
  if (tokens.length < 2) return null;
  const [segment, ...rest] = tokens;
  const remainder = rest.join(" ").trim();
  if (!segment || !remainder) return null;
  return { topText: segment, bottomText: remainder };
};

const splitNumericLabel = (label: string): MinuteDisplay | null => {
  const match = label.match(/^(\d+)\s*(.+)$/);
  if (!match) return null;
  const [, digits, rest] = match;
  const suffix = rest.trim();
  if (!digits || !suffix) return null;
  if (!/[a-z]/i.test(suffix)) return null;
  return { topText: digits, bottomText: suffix };
};

const isHalfTimeStatus = (
  status: MatchStatus | undefined,
  fallbackSport: SPORT,
  rawLabel: string
) => {
  const sport = status?.s ?? fallbackSport;
  const normalizedCode =
    typeof status?.nsc === "string" ? status.nsc.trim() : undefined;
  if (sport && normalizedCode) {
    const codes = HALF_TIME_STATUS_CODES[sport];
    if (codes?.includes(normalizedCode)) return true;
  }

  const normalizedLabel = rawLabel.trim().toLowerCase();
  if (
    normalizedLabel.length &&
    HALF_TIME_KEYWORDS.some((keyword) => normalizedLabel.includes(keyword))
  ) {
    return true;
  }

  return false;
};

export const resolveMinuteDisplay = (
  status: MatchStatus | undefined,
  fallbackSport: SPORT,
  rawMinuteLabel: string
): MinuteDisplay => {
  const sport = status?.s ?? fallbackSport;
  if (isMatchFinished(status, sport)) {
    return {
      topText: "Full Time",
      bottomText: null,
      responsiveTopText: { mobile: "FT", desktop: "Full Time" },
    };
  }

  const trimmedLabel = rawMinuteLabel.trim();
  if (!trimmedLabel) return DEFAULT_MINUTE_DISPLAY;

  if (isHalfTimeStatus(status, sport, trimmedLabel)) {
    return {
      topText: "Half Time",
      bottomText: null,
      responsiveTopText: { mobile: "HF", desktop: "Half Time" },
    };
  }

  const setDisplay = splitSetScoreLabel(trimmedLabel);
  if (setDisplay) return setDisplay;

  if (sport === SPORT.BASKETBALL) {
    const basketballDisplay = splitBasketballLabel(trimmedLabel);
    if (basketballDisplay) return basketballDisplay;
  }

  const numericDisplay = splitNumericLabel(trimmedLabel);
  if (numericDisplay) return numericDisplay;

  return { topText: trimmedLabel, bottomText: null };
};
